import React, { useState, useEffect, useCallback, useRef } from "react";
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const PAGE_SIZE = 5;

const FollowUpModal = ({
  show,
  selectedLead,
  onClose,
  isDarkMode,
  editingFollowUpIndex,
  followUpFormData,
  setFollowUpFormData,
  handleUpdateFollowUp,
  addFollowUp,
  loading,
  setEditingFollowUpIndex,
  setSelectedLead
}) => {
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminPage, setAdminPage] = useState(1);
  const [adminTotalPages, setAdminTotalPages] = useState(1);
  const [adminLoading, setAdminLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchAdminUsers = useCallback(async (page = 1) => {
    setAdminLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/auth/admin-users?page=${page}&page_size=${PAGE_SIZE}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (res.ok) {
        const data = await res.json();
        setAdminUsers(data.results || []);
        setAdminTotalPages(data.total_pages || 1);
        setAdminPage(data.page || 1);
      }
    } catch (err) {
      console.error("Failed to fetch admin users:", err);
    } finally {
      setAdminLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      fetchAdminUsers(1);
    }
  }, [show, fetchAdminUsers]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  if (!show || !selectedLead) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-2 sm:px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => {
          onClose();
          setEditingFollowUpIndex(null);
          setSelectedLead(null);
        }}></div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all my-4 sm:my-8 sm:align-middle w-full max-w-md sm:max-w-xl md:max-w-3xl">
          <div className="px-3 sm:px-4 pt-4 pb-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{editingFollowUpIndex !== null ? 'Edit Follow-up' : 'Schedule Follow-up'}</h3>
              <button
                onClick={() => {
                  onClose();
                  setEditingFollowUpIndex(null);
                  setSelectedLead(null);
                }}
                className="hover:opacity-80"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-3 p-2 rounded-lg" style={{
              backgroundColor: isDarkMode ? '#1f2937' : '#eff6ff'
            }}>
              <p className="text-xs" style={{
                color: isDarkMode ? '#d1d5db' : '#374151'
              }}>
                <span className="font-semibold">Company:</span> {selectedLead.company_name}
              </p>
              <p className="text-xs" style={{
                color: isDarkMode ? '#d1d5db' : '#374151'
              }}>
                <span className="font-semibold">Contact:</span> {selectedLead.primary_contact_name}
              </p>
            </div>

            <form onSubmit={editingFollowUpIndex !== null ? handleUpdateFollowUp : (e) => { e.preventDefault(); addFollowUp(); }} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Scheduled Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={followUpFormData.scheduled_date}
                    onChange={(e) => setFollowUpFormData({...followUpFormData, scheduled_date: e.target.value})}
                    className="w-full px-2 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Follow-up Type *
                  </label>
                  <select
                    required
                    value={followUpFormData.follow_up_type}
                    onChange={(e) => setFollowUpFormData({...followUpFormData, follow_up_type: e.target.value})}
                    className="w-full px-2 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                  >
                    <option value="Call">Call</option>
                    <option value="Email">Email</option>
                    <option value="Meeting">Meeting</option>
                    <option value="Demo">Demo</option>
                    <option value="Proposal">Proposal</option>
                    <option value="Contract Review">Contract Review</option>
                    <option value="Check-in">Check-in</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Status *
                  </label>
                  <select
                    required
                    value={followUpFormData.status}
                    onChange={(e) => setFollowUpFormData({...followUpFormData, status: e.target.value})}
                    className="w-full px-2 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                  >
                    <option value="Scheduled">Scheduled</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                    <option value="Rescheduled">Rescheduled</option>
                  </select>
                </div>

                <div ref={dropdownRef} className="relative">
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Assigned To
                  </label>
                  <select
                    className="w-full px-2 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800 appearance-none cursor-pointer"
                    value={followUpFormData.assigned_to}
                    onClick={(e) => { e.preventDefault(); setDropdownOpen(!dropdownOpen); }}
                    onMouseDown={(e) => e.preventDefault()}
                    readOnly
                  >
                    <option>{followUpFormData.assigned_to || "Select admin user"}</option>
                  </select>

                  {dropdownOpen && (
                    <div className="absolute z-50 mt-0.5 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm flex flex-col" style={{ maxHeight: '280px' }}>
                      {adminLoading ? (
                        <div className="px-2 py-1.5 text-xs text-gray-500 dark:text-gray-400 text-center">Loading...</div>
                      ) : adminUsers.length === 0 ? (
                        <div className="px-2 py-1.5 text-xs text-gray-500 dark:text-gray-400 text-center">No admin users found</div>
                      ) : (
                        <>
                          <div className="flex-1 overflow-y-auto">
                            {adminUsers.map((user) => {
                              const fullName = `${user.firstname} ${user.lastname}`.trim();
                              const isSelected = followUpFormData.assigned_to === fullName;
                              return (
                                <div
                                  key={user.id}
                                  onClick={() => {
                                    setFollowUpFormData({...followUpFormData, assigned_to: fullName});
                                    setDropdownOpen(false);
                                  }}
                                  className={`px-2 py-1 text-xs cursor-default ${
                                    isSelected
                                      ? 'bg-stone-50 text-neutral-700'
                                      : 'bg-zinc-100 dark:bg-white text-gray-900 dark:text-white hover:bg-stone-300 hover:text-stone-900'
                                  }`}
                                >
                                  {fullName}
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex-shrink-0 flex items-center justify-between px-2 py-1 border-t border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 rounded-b-md">
                            <button
                              type="button"
                              disabled={adminPage <= 1}
                              onClick={(e) => { e.stopPropagation(); fetchAdminUsers(adminPage - 1); }}
                              className="flex items-center text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <ChevronLeftIcon className="w-3 h-3 mr-0.5" /> Prev
                            </button>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{adminPage} / {adminTotalPages}</span>
                            <button
                              type="button"
                              disabled={adminPage >= adminTotalPages}
                              onClick={(e) => { e.stopPropagation(); fetchAdminUsers(adminPage + 1); }}
                              className="flex items-center text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Next <ChevronRightIcon className="w-3 h-3 ml-0.5" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {editingFollowUpIndex !== null && (() => {
                // Check if any other follow-up (excluding current one) has outcome="Engaged"
                const hasEngagedOutcome = selectedLead?.follow_ups?.some((f, idx) => 
                  idx !== editingFollowUpIndex && f.outcome === 'Engaged'
                );
                
                return (
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                      Outcome
                    </label>
                    <select
                      value={followUpFormData.outcome}
                      onChange={(e) => setFollowUpFormData({...followUpFormData, outcome: e.target.value})}
                      className="w-full px-2 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                    >
                      <option value="">Select outcome...</option>
                      <option value="Positive">Positive</option>
                      <option value="Neutral">Neutral</option>
                      <option value="Negative">Negative</option>
                      <option value="No Response">No Response</option>
                      {!hasEngagedOutcome && <option value="Engaged">Engaged</option>}
                    </select>
                  </div>
                );
              })()}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Notes
                  </label>
                  <textarea
                    value={followUpFormData.notes}
                    onChange={(e) => setFollowUpFormData({...followUpFormData, notes: e.target.value})}
                    rows={3}
                    className="w-full px-2 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                    placeholder="Follow-up notes and objectives..."
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Next Action
                  </label>
                  <textarea
                    value={followUpFormData.next_action}
                    onChange={(e) => setFollowUpFormData({...followUpFormData, next_action: e.target.value})}
                    rows={3}
                    className="w-full px-2 py-1.5 text-xs border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                    placeholder="Next steps after this follow-up"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2 pt-3 border-t dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    setEditingFollowUpIndex(null);
                    setSelectedLead(null);
                  }}
                  className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-3 py-1.5 text-xs bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingFollowUpIndex !== null ? (loading ? 'Updating...' : 'Update Follow-up') : (loading ? 'Scheduling...' : 'Schedule Follow-up')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FollowUpModal;
